import { Trans } from 'react-i18next';

import { useFormController } from '@dreryk/react-common';
import {
   Form,
   IconFillColor,
   IconSize,
   IconWrapper,
   QuestionmarkCircleIcon,
   StyledFlex,
   Toggle,
   Tooltip,
} from '@dreryk/react-components';

import { patientPhoneNotificationTranslationKeys } from '../../translations';

import { useCurrentHealthcareProviderWaitlistTimeSlotReservedPatientNotificationEnabledSetCommand } from './PatientPhoneNotificationWaitlistProposedAppointmentToggle.hooks';
import { PatientPhoneNotificationWaitlistProposedAppointmentToggleLoader } from './PatientPhoneNotificationWaitlistProposedAppointmentToggle.loader';
import { PatientPhoneNotificationWaitlistProposedAppointmentToggleFormBean } from './PatientPhoneNotificationWaitlistProposedAppointmentToggle.types';
import { PhoneNotificationTooltipText } from './PhoneNotificationTooltipText';

type PatientPhoneNotificationWaitlistProposedAppointmentToggleProps = {
   readonly notificationEnabled: boolean;
   readonly messages: string[];
   readonly refetch: VoidFunction;
};

export const PatientPhoneNotificationWaitlistProposedAppointmentToggle = (
   props: PatientPhoneNotificationWaitlistProposedAppointmentToggleProps
): JSX.Element => {
   const {
      register,
      handleSubmit,
   } = useFormController<PatientPhoneNotificationWaitlistProposedAppointmentToggleFormBean>({
      notificationEnabled: props.notificationEnabled,
   });
   const {
      executeAsync: onSubmit,
      loading,
   } = useCurrentHealthcareProviderWaitlistTimeSlotReservedPatientNotificationEnabledSetCommand(props.refetch);

   return (
      <StyledFlex gap={'12px'}>
         <Form onInput={handleSubmit(onSubmit)}>
            <Toggle
               controller={register('notificationEnabled')}
               testId={'patientPhoneNotificationWaitlistProposedAppointmentToggle'}
               disabled={loading}
            />
         </Form>
         <StyledFlex gap={'4px'} alignItems={'center'}>
            <Trans
               i18nKey={patientPhoneNotificationTranslationKeys.notificationToggle.waitlistProposedAppointmentToggle}
            />
            <Tooltip
               maxWidth={'500px'}
               testId={'waitlistProposedAppointmentToggleTooltip'}
               content={
                  <PhoneNotificationTooltipText
                     header={
                        patientPhoneNotificationTranslationKeys.notificationToggle
                           .waitlistProposedAppointmentToggleTooltip
                     }
                     messages={props.messages}
                  />
               }
            >
               <IconWrapper
                  testId={'waitlistProposedAppointmentToggleTooltipIconWrapper'}
                  fillColor={IconFillColor.PRIMARY}
                  size={IconSize.SMALL}
               >
                  <QuestionmarkCircleIcon />
               </IconWrapper>
            </Tooltip>
         </StyledFlex>
      </StyledFlex>
   );
};

export const PatientPhoneNotificationWaitlistProposedAppointmentToggleWithLoader = (): JSX.Element => {
   return (
      <PatientPhoneNotificationWaitlistProposedAppointmentToggleLoader>
         {({ notificationEnabled, messages, refetch }) => (
            <PatientPhoneNotificationWaitlistProposedAppointmentToggle
               notificationEnabled={notificationEnabled}
               messages={messages}
               refetch={refetch}
            />
         )}
      </PatientPhoneNotificationWaitlistProposedAppointmentToggleLoader>
   );
};
